import React, { FC, useState, Fragment } from 'react';

import classNames from 'classnames';
import { Listbox, Transition } from '@headlessui/react';
import { useReactConstructor } from '../../utils/react/constructor.hook';
import { CheckmarkStrongIcon, ChevronUpRegularIcon, ChevronDownRegularIcon } from 'src/monet/icons';
import { useUpdateEffect } from 'react-use';
import { TextInput } from './text-input';

interface NeueListboxProps {
  selected?: any;
  disabled?: boolean;
  options: any[];
  isStatic?: boolean;
  defaultLabel?: string;
  listboxButton?: any;
  buttonColorClassName?: string;
  listBoxClassName?: string;
  listboxOptionsClassName?: string;
  labelClassName?: string;
  containerClassName?: string;
  iconClassName?: string;
  buttonClassName?: string;
  renderValue: any;
  renderSelectedValue?: any;
  hideChevron?: boolean;
  enableSearch?: boolean;
  renderKey: any;
  preventChangeEventOnMount?: boolean;
  onChange: (selected: any) => void;
  onMount?: (selected: any) => void;
}

export const NeueListbox: FC<NeueListboxProps> = ({
  selected = null,
  disabled = false,
  options,
  isStatic = false,
  defaultLabel = '',
  listboxButton = null,
  listboxOptionsClassName = '',
  hideChevron = false,
  enableSearch = false,
  containerClassName = 'min-w-0',
  buttonClassName = 'py-[7px]',
  iconClassName,
  labelClassName = 'text-neue-journey-medium',
  buttonColorClassName = 'bg-neue-journey-fg-10 text-neue-journey-fg',
  renderValue,
  preventChangeEventOnMount,
  renderSelectedValue,
  renderKey,
  onChange,
  onMount,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [selectedOption, setSelectedOption] = useState(selected);
  const [filteredOptions, setFilteredOptions] = useState(options);

  useReactConstructor(() => {
    onMount && onMount(selected);
  });

  const _onChange = (option: any) => {
    setSelectedOption(option);
    if (onChange) {
      onChange(option);
    }
  };

  useUpdateEffect(() => {
    if (!preventChangeEventOnMount) {
      _onChange(selected);
    }
  }, [selected]);

  const optionsToList = enableSearch ? filteredOptions : options;
  const isSearchResultEmpty = enableSearch && searchInput && optionsToList.length === 0;

  return (
    <Listbox value={selectedOption} onChange={_onChange} disabled={disabled}>
      {({ open }) => (
        <>
          <div className={classNames('relative', containerClassName)}>
            {listboxButton ? (
              listboxButton
            ) : (
              <Listbox.Button
                className={classNames(
                  'px-4 flex items-start w-full rounded-lg border-2',
                  { 'bg-neue-journey-accent-10 border-neue-journey-accent': open, 'border-transparent': !open },
                  buttonClassName,
                  buttonColorClassName
                )}
              >
                <span className={classNames('truncate pr-4', labelClassName)}>
                  {selectedOption
                    ? renderSelectedValue
                      ? renderSelectedValue(selectedOption)
                      : renderValue(selectedOption)
                    : defaultLabel}
                </span>
                {!hideChevron && (
                  <span className='absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-neue-journey-fg'>
                    {open ? (
                      <ChevronUpRegularIcon className={iconClassName} />
                    ) : (
                      <ChevronDownRegularIcon className={iconClassName} />
                    )}
                  </span>
                )}
              </Listbox.Button>
            )}
            <Transition
              show={open}
              as='div'
              leave='transition ease-in duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Listbox.Options
                static={isStatic}
                className={classNames(
                  'absolute z-10 mt-1 w-full bg-neue-journey-bg max-h-[176px] rounded-lg overflow-auto focus:outline-none',
                  listboxOptionsClassName,
                  { 'p-2': !enableSearch }
                )}
              >
                {enableSearch && (
                  <div className='sticky top-0 z-10 bg-neue-journey-bg'>
                    <TextInput
                      id='search-font'
                      isNeue
                      inputClasses='!pr-0 w-[147px] mx-auto !rounded-b-none'
                      inputProps={{ value: searchInput, placeholder: 'Search font' }}
                      onChange={(e: any) => {
                        const searchTerm = e.target.value.toLowerCase();
                        setSearchInput(searchTerm);
                        setFilteredOptions(options.filter((option) => option.toLowerCase().includes(searchTerm)));
                      }}
                    />
                  </div>
                )}
                {optionsToList.map((option) => (
                  <Listbox.Option
                    key={renderKey(option)}
                    className={classNames(
                      'text-neue-journey-fg cursor-pointer select-none relative py-[7px] hover:bg-neue-journey-bg-90 rounded',
                      {
                        'opacity-50 pointer-events-none': option.disabled,
                        'px-2': !enableSearch,
                        'mx-2': enableSearch,
                      }
                    )}
                    value={option}
                  >
                    {({ selected }) => (
                      <div className='flex items-start relative'>
                        <span className='absolute right-0'>{selected && <CheckmarkStrongIcon />}</span>
                        <span className='text-bedrock-p block mr-9'>{renderValue(option)}</span>
                      </div>
                    )}
                  </Listbox.Option>
                ))}
                {isSearchResultEmpty && (
                  <Listbox.Option
                    disabled
                    className={classNames(
                      'text-neue-journey-fg cursor-pointer opacity-50 pointer-events-none mx-2 select-none relative py-[7px] hover:bg-neue-journey-bg-90 rounded'
                    )}
                    value={''}
                  >
                    <span className='text-bedrock-p block'>No results found</span>
                  </Listbox.Option>
                )}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
};
